import {
    IEditorCharacterEventProps,
    IEditorEventHandler,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-event.interface';
import { Editor, ParagraphElement } from '@tencent/alloy-editor';
import escapeRegExp from 'lodash/escapeRegExp';
import {
    hasCharacterOfParaElement,
    isCaretAtBeginningOfPara,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import { CharacterTypes } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/character-type';
import { getNumberingCharacterConfig } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/character-utils/numbering';
import {
    IndentType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';

/**
 * 项目符号事件处理类
 */
export class CharacterEventHandler implements IEditorEventHandler {
    private editor: Editor;
    private getSelection: IEditorCharacterEventProps['getSelection'];
    private operationHandler: IEditorCharacterEventProps['operationHandler'];

    // eslint-disable-next-line no-useless-constructor
    public constructor({
        editor,
        getSelection,
        operationHandler,
    }: IEditorCharacterEventProps) {
        this.editor = editor;
        this.getSelection = getSelection;
        this.operationHandler = operationHandler;
    }

    public handleTabEvent(event: KeyboardEvent): boolean {
        this.operationHandler.setIndent(event?.shiftKey ? IndentType.INDENT_REDUCE : IndentType.INDENT_INCREASE);
        return true;
    }

    public handleEnterEvent(event: KeyboardEvent): boolean {
        const { editor } = this;
        if (!editor.selection) {
            return false;
        }
        // 获取当前选区或光标的起始位置
        const startPoint = editor.getStartPoint()!;
        // 获取当前段落
        const paragraph = editor.getParent(startPoint.path)[0] as ParagraphElement;
        if (!hasCharacterOfParaElement(paragraph)) {
            // 当前段落没项目符号，判断是否需要自动添加
            this.autoNumbering();
            return false;
        }
        return false;
    }

    public handleBackspaceEvent(event: KeyboardEvent): boolean {
        const documentValue = this.editor.getValue();
        const selection = this.getSelection();
        if (selection === null) return false;
        const { anchor, focus } = selection;
        if (!isCaretAtBeginningOfPara(anchor, focus)) return false;

        const [index] = anchor.path;
        const paragraph = documentValue.children[index];
        if (!hasCharacterOfParaElement(paragraph)) return false;
        event.preventDefault?.();
        event.stopPropagation?.();
        // 只是删除项目符号类型
        this.operationHandler.setCharacter?.({ type: CharacterTypes.ITEMSYMBOL_NONE });
        return true;
    }

    /**
     * 自动添加数字编号
     */
    private autoNumbering(): void {
        const { editor } = this;
        // 获取当前选区或光标的起始位置
        const startPoint = editor.getStartPoint()!;
        // 获取段落 path
        const paragraphPath = startPoint.path.slice(0, -1);
        // 获取段落起始位置 point
        const paragraphStart = editor.getStartPoint(paragraphPath)!;
        // 获取段落开头到当前光标位置的文字
        const text = editor.getString({
            anchor: paragraphStart,
            focus: startPoint,
        });
        if (!text) {
            return;
        }
        const matchedNumbering = this.getMatchedNumbering(text);
        if (matchedNumbering) {
            // 命中了默认的数字编号
            const { type, matchedString } = matchedNumbering;
            // 根据 office 规则，匹配到的(数字编号文字 + n个空格)需要删除
            const endPoint = editor.getAfterPoint(paragraphStart, matchedString.length)!;
            editor.deleteText({ anchor: paragraphStart, focus: endPoint });
            // 设置匹配到的数字编号
            this.operationHandler.setCharacter?.({ type });
        }
    }

    /**
     * 根据 office 规则获取匹配到的数字编号
     */
    private getMatchedNumbering(text: string) {
        const numberingCharacterConfig = getNumberingCharacterConfig();
        let matchedString = '';
        const matchedNumbering = numberingCharacterConfig.find(({ startText }) => {
            // office 规则，匹配开头为数字编号+大于1个空格+大于一个非空格
            if (text.startsWith(`${startText} `)) {
                // 正则相对更耗性能，先使用 startWith 匹配之后再用正则进行严格匹配
                const regex = new RegExp(`^(${escapeRegExp(startText)}\\s+)\\S+`);
                const matchResult = text.match(regex);
                if (matchResult) {
                    [, matchedString] = matchResult;
                    return true;
                }
            }
            return false;
        });
        return matchedNumbering && {
            type: matchedNumbering.type,
            matchedString,
        };
    }
}
